import { Component } from "react";

/**
 * ViewerErrorBoundary
 * Catches JavaScript render errors in the Viewer component tree.
 *
 * IMPORTANT: React ErrorBoundary catches synchronous render errors only.
 * It does NOT catch:
 *   - Errors in event handlers
 *   - Errors in async callbacks / Promises
 *   - Errors in RAF callbacks (requestAnimationFrame)
 *   - iOS Safari WebKit process OOM/kill (tab crash)
 *
 * For those, window.onerror + window.onunhandledrejection handlers in
 * Viewer.jsx cover the cases that CAN be caught before the process dies.
 */
export class ViewerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const ts = typeof performance !== "undefined" ? performance.now().toFixed(1) : Date.now();

    // Full stack + component stack — visible in Safari Web Inspector console
    console.log(`[AVAIL_ERROR] React ErrorBoundary caught at t=${ts}ms`);
    console.log(`[AVAIL_ERROR] message: ${error?.message}`);
    console.log(`[AVAIL_ERROR] stack:\n${error?.stack || "(no stack)"}`);
    console.log(`[AVAIL_ERROR] componentStack:\n${info?.componentStack || "(no componentStack)"}`);

    this.setState({ componentStack: info?.componentStack || null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgb(4,4,12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--v360-font, Manrope, sans-serif)",
          }}
        >
          <div style={{ textAlign: "center", maxWidth: 320, padding: "0 24px" }}>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              Unable to load
            </p>
            <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 13, marginBottom: 4 }}>
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            <p style={{ color: "rgba(255,255,255,0.15)", fontSize: 11, marginBottom: 20, wordBreak: "break-all" }}>
              {this.state.componentStack?.split("\n")[1]?.trim() || ""}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.4)",
                background: "transparent",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
